import { Component, Inject } from '@angular/core';
import {
  RadioGroupV2Module,
  SelectV2Module,
  InputV2Module,
  TextfieldV2Module,
  SnackbarService,
  AutocompleteSelectV3Component,
  MultiSelectV3Component,
} from '@gea/digital-ui-lib';
import { ApiService } from 'src/app/shared/services/api.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TreeModule } from 'primeng/tree';
import {
  Feedback,
  FeedbackCopy,
  FeedbackView,
} from '../../data/data.generated';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'hrt-jira-dialog',
  imports: [
    TreeModule,
    RadioGroupV2Module,
    InputV2Module,
    TextfieldV2Module,
    ReactiveFormsModule,
    SelectV2Module,
    AutocompleteSelectV3Component,
    MultiSelectV3Component,
  ],
  templateUrl: './hrt-jira-dialog.component.html',
  styleUrl: './hrt-jira-dialog.component.scss',
  standalone: true,
})
export class HrtJiraDialogComponent {
  feedbackData: FeedbackView;
  jiraForm: FormGroup;
  jiraData: JiraData;
  isLoading = true;
  isLoadingUsers = true;
  isFieldDisabled = false;
  projectKey: string = '';
  summaryLabel: string = '';
  companyOptions: { value: string; name: string }[] = [];
  ticketTypeOptions = [
    { value: 'Task', name: 'Task' },
    { value: 'Story', name: 'Story' },
    { value: 'Bug', name: 'Bug' },
  ];
  subHeaderOptions = [
    { value: 'RTSelect', name: 'RT Select' },
    { value: 'HRTConnect', name: 'HRT Connect' },
    { value: 'GEAOmni', name: 'GEA Omni' },
  ];
  priorityOptions = [
    { value: 'Low', name: 'Low' },
    { value: 'Medium', name: 'Medium' },
    { value: 'High', name: 'High' },
    { value: 'Critical', name: 'Critical' },
  ];
  reportToOptions = [];
  assignToOptions = [];

  constructor(
    private api: ApiService,
    private fb: FormBuilder,
    private dynamicDialogRef: DynamicDialogRef,
    private snackbarService: SnackbarService,
    private dynamicDialogConfig: DynamicDialogConfig<{
      feedbackData: FeedbackView;
      projectKey: string;
      refreshFeedback: () => void;
    }>,
  ) {
    if (this.dynamicDialogConfig.data) {
      this.feedbackData = this.dynamicDialogConfig.data.feedbackData;
      this.projectKey = this.dynamicDialogConfig.data.projectKey;
    }
  }

  ngOnInit(): void {
    this.companyOptions = this.feedbackData.CompanyNames.split(',').map(
      (name) => ({
        value: name,
        name: name,
      }),
    );

    this.api.Feedback.GetJiraUsers(this.projectKey).subscribe({
      next: (users: any) => {
        this.assignToOptions = users.map((user: any) => ({
          value: user.accountId,
          name: user.displayName,
        }));
        this.reportToOptions = [...this.assignToOptions];
        this.isLoadingUsers = false;
      },
      error: () => {
        this.snackbarService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Jira users.',
        });
        this.isLoadingUsers = false;
        this.isFieldDisabled = true;
      },
    });

    if (this.companyOptions.length > 2) {
      this.companyOptions.unshift({ value: 'All', name: 'All' });
    }

    this.jiraForm = this.fb.group({
      ticketType: [
        this.getJiraType(this.feedbackData?.TypeId),
        Validators.required,
      ],
      summary: [this.feedbackData?.Title || '', Validators.required],
      description: [this.feedbackData?.Text || '', Validators.required],
      assignTo: [''],
      reportTo: [''],
      subHeader: [
        this.getSubHeader(this.feedbackData.ApplicationId),
        Validators.required,
      ],
      priority: [this.feedbackData?.PriorityId === 4 ? 'Critical' : ''],
      company: [
        this.getCompanyNames(this.feedbackData?.CompanyNames),
        Validators.required,
      ],
    });

    const updateSummaryLabel = () => {
      if (this.projectKey === 'HRTC') {
        let selectedCompanies: string[] = [];
        const summaryValue = this.jiraForm.get('summary')?.value || '';
        const subHeaderValue =
          this.jiraForm.get('subHeader')?.value?.value ||
          this.jiraForm.get('subHeader')?.value ||
          '';
        const companyValue = this.jiraForm.get('company')?.value;
        if (Array.isArray(companyValue)) {
          selectedCompanies = companyValue.map((c: any) => c.name || c);
        } else if (companyValue) {
          selectedCompanies = [companyValue.name || companyValue];
        }
        const companiesLabel =
          selectedCompanies.length > 0 ? selectedCompanies.join(', ') : '';
        if (summaryValue.trim() !== '') {
          this.summaryLabel = `[${subHeaderValue}]${companiesLabel ? ' - [' + companiesLabel + ']' : ''} - ${summaryValue}`;
        } else {
          this.summaryLabel = '';
        }
      } else if (this.projectKey === 'HRTAUTO') {
        this.summaryLabel = `[GPLEX/ Omni] - ${this.jiraForm.get('summary')?.value || ''}`;
      }
    };

    updateSummaryLabel();

    this.jiraForm.get('summary')?.valueChanges.subscribe(updateSummaryLabel);
    this.jiraForm.get('subHeader')?.valueChanges.subscribe(updateSummaryLabel);
    this.jiraForm.get('company')?.valueChanges.subscribe(updateSummaryLabel);

    this.dynamicDialogRef.onClose.subscribe((result) => {
      if (result.buttonType == 'Accept') {
        if (this.jiraForm.invalid) {
          this.snackbarService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Please fill all required fields.',
          });
          return;
        }
        this.jiraData = this.flattenFormValues(this.jiraForm.value);
        this.jiraData.summary = this.summaryLabel;
        this.jiraData.url = window.location.href;
        this.snackbarService.add({
          severity: 'info',
          summary: 'Info',
          detail: 'Creating Jira ticket, please wait...',
        });
        this.api.Feedback.CreateJiraTicket(this.jiraData).subscribe({
          next: (res) => {
            if (res.key) {
              const newJiraId = res.key.split('-')[1];
              if (this.feedbackData.Source && this.feedbackData.Source.trim()) {
                const existingIds = this.feedbackData.Source.split(',')
                  .map((id) => id.trim())
                  .filter((id) => id !== '');

                if (!existingIds.includes(newJiraId)) {
                  existingIds.push(newJiraId);
                }

                this.feedbackData.Source = existingIds.join(',');
              } else {
                this.feedbackData.Source = newJiraId;
              }
              this.feedbackData.StatusId = 2; //approved
              const feedbackObj = this.deleteViewKeys(this.feedbackData);
              this.api.Feedback.UpdateFeedback(feedbackObj, []).subscribe({
                next: () => {
                  this.api.Feedback.GetFeedbackView(
                    this.feedbackData.Id,
                  ).subscribe({
                    next: () => {
                      if (this.dynamicDialogConfig.data?.refreshFeedback) {
                        this.dynamicDialogConfig.data.refreshFeedback();
                        this.snackbarService.add({
                          severity: 'success',
                          summary: 'Success',
                          detail: 'Status and Jira chip updated successfully.',
                        });
                      }
                    },
                    error: () => {
                      this.snackbarService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail:
                          'Failed to update jira ticket chip on Feedback, refresh page.',
                      });
                    },
                  });
                },
                error: () => {
                  this.snackbarService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update feedback, refresh page.',
                  });
                },
              });
            }
          },
          error: (err) => {
            this.snackbarService.add({
              severity: 'error',
              summary: 'Error',
              detail: err.error || 'Failed to create Jira ticket.',
            });
          },
          complete: () => {
            this.snackbarService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Jira ticket created successfully.',
            });
            this.snackbarService.add({
              severity: 'info',
              summary: 'Info',
              detail: 'Updating feedback, please wait...',
            });
          },
        });
      } else {
        this.snackbarService.add({
          severity: 'info',
          summary: 'Cancelled',
          detail: 'Jira ticket creation cancelled.',
        });
      }
    });
  }

  deleteViewKeys(feedbackView: any): Feedback {
    let newFeedback = new FeedbackCopy();
    const feedbackKeys = Object.keys(newFeedback);
    Object.keys(feedbackView).map((key: string) => {
      if (feedbackKeys.includes(key)) {
        newFeedback[key] = feedbackView[key];
      }
    });
    return newFeedback;
  }

  flattenFormValues(formValue: any): any {
    const result: any = {};
    for (const key of Object.keys(formValue)) {
      const val = formValue[key];
      if (Array.isArray(val)) {
        result[key] = val.map((item) =>
          item && typeof item === 'object' && 'value' in item
            ? item.value
            : item,
        );
      } else if (val && typeof val === 'object' && 'value' in val) {
        result[key] = val.value;
      } else {
        result[key] = val;
      }
    }
    return result;
  }

  getJiraType(typeId: number | undefined): string {
    switch (typeId) {
      case 0:
        return 'Story'; // Feedback
      case 1:
        return 'Story'; // FeatureRequest
      case 2:
        return 'Bug'; // ProblemBug
      default:
        return 'Story';
    }
  }

  getCompanyNames(name: string): string[] {
    if (!name) return [];
    const names = name.split(',');
    return names.map((n) => n.trim());
  }

  getSubHeader(appId: string): string {
    switch (appId) {
      case '1':
        return 'RT Select';
      case '2':
        return 'HRT Connect';
      case '3':
        return 'GEA Omni';
      default:
        return 'RT Select';
    }
  }

  onValueChange(event: any, field: string) {
    this.jiraForm.get(field)?.setValue(event.value);
  }

  ngOnDestroy(): void {
    // Cleanup logic here if needed
  }
}

export interface JiraData {
  ticketType: string;
  summary: string;
  description: string;
  assignTo: string;
  reportTo: string;
  priority: string;
  url: string;
}

export interface JiraUserData {
  accountId: string;
  displayName: string;
  emailAddress: string;
  active: boolean;
}
